import React, { useCallback, useEffect, useRef, useState } from 'react';
import uuid from 'uuid';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import RoutePath from 'App/RoutePath';
import { useHistory } from 'react-router';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import useUserEntitlements from 'hooks/useUserEntitlements';
import {
  useConversationVariables,
  useShowVoiceDisclaimer,
} from 'Assistant/Chatbot/hooks';
import { USER_QUERY } from 'Assistant/Chatbot/graphql';
import { User } from 'Assistant/Chatbot/graphql/__generated__/User';
import {
  ConversationStyle,
  MappedMessageType,
} from 'Assistant/__generated__/client.schemas';
import { tracking } from 'App/Tracking';
import { isMSTeams } from 'utils/MSTeams';
import { OnSubmit } from '../../types';
import { InputBar, TextFieldWrapper } from '../styles';
import { StyleSelect } from '../StyleSelect';
import {
  ChatBotTextInput,
  VoiceButton,
  ControlsContainer,
  StyleSelectContainer,
  StyleSelectErrorContainer,
  ErrorIcon,
  ErrorText,
  ChatSubmitButton,
} from './styles';

interface ChatbotInputProps {
  /**
   * Boolean to indicate whether submit button is disabled.
   */
  canSubmit?: boolean;
  /**
   * Callback function to handle submission of form.
   */
  onSubmit: OnSubmit;
  /**
   * Boolean to indicate whether the input is displayed on the homepage.
   */
  displayOnHomepage?: boolean;
  /**
   * Callback function to set the state of the multiline input.
   */
  setMultilineInputState?(isMultiline: boolean): void;
}

export const ChatbotInput = ({
  canSubmit,
  onSubmit,
  displayOnHomepage = false,
  setMultilineInputState,
}: ChatbotInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentInput, setCurrentInput] = useState('');
  const { t: translate } = useTranslation('chatbot');
  const { entitlements } = useUserEntitlements();
  const isNovaVoiceEnabled = entitlements?.novaVoice;
  const stylesEnabled = useFeatureFlag('nova-conversation-styles-enabled');
  const welcomeConversationEnabled = useFeatureFlag(
    FEATURE_FLAGS.NOVA_WEB_WELCOME_CONVERSATION,
  );
  const history = useHistory();
  const {
    isLoading: isLoadingConversationVariables,
    conversationVariables,
    error: conversationVariablesError,
    setConversationVariables,
  } = useConversationVariables();
  const { showVoiceDisclaimer } = useShowVoiceDisclaimer();
  const isInMSTeams = isMSTeams();

  // On the chat page (not the homepage), users still in Nova onboarding should
  // not be offered the voice button — they should complete the welcome
  // conversation first. We only need the status where voice could show, so the
  // query is skipped on the homepage and when voice isn't enabled.
  // We also skip the query if the welcome conversation feature flag is off,
  // since we don't need to check onboarding status in that case.
  const {
    data: userData,
    loading: isLoadingOnboardingStatus,
    error: onboardingStatusError,
  } = useQuery<User>(USER_QUERY, {
    skip:
      displayOnHomepage || !isNovaVoiceEnabled || !welcomeConversationEnabled,
  });
  const onboardingStatus = userData?.user?.onboardingStatus?.value;
  const isOnboarding =
    welcomeConversationEnabled &&
    (onboardingStatus === 'notStarted' || onboardingStatus === 'inProgress');
  // Suppress while loading too, so the voice button doesn't briefly flash before
  // we know the status. Also fail closed on error or if LD is down:
  // if we can't determine the status or the flag, don't risk offering voice
  // to a user who is still onboarding.
  const suppressVoiceForOnboarding =
    !displayOnHomepage &&
    (isLoadingOnboardingStatus ||
      isOnboarding ||
      Boolean(onboardingStatusError) ||
      welcomeConversationEnabled === undefined);

  const navigateToVoice = () => {
    history.push(
      showVoiceDisclaimer ? RoutePath.VoiceDisclaimer : RoutePath.Voice,
    );
  };

  const handleOnSubmit = () => {
    void onSubmit({
      id: uuid.v4(),
      createdAt: new Date().toISOString(),
      content: currentInput,
      topic: null,
      starterId: null,
      type: MappedMessageType.text,
    });

    setCurrentInput('');

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentInput(e.target.value);

    if (setMultilineInputState) {
      // Check if textarea has grown beyond single line
      const element = e.target;
      const style = getComputedStyle(element);
      const lineHeight = parseFloat(style.lineHeight);
      const padding =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const singleLineHeight = lineHeight + padding;

      setMultilineInputState(element.scrollHeight > singleLineHeight);
    }
  };

  useEffect(() => {
    // Focus the input element if it exists
    inputRef.current?.focus();
  }, []);

  const onKeyDown: React.KeyboardEventHandler<HTMLElement> = event => {
    const pressedEnter = event.key === 'Enter';

    if (pressedEnter && currentInput === '') {
      event.preventDefault();

      return;
    }

    if (pressedEnter && !event.shiftKey && canSubmit) {
      handleOnSubmit();
      event.preventDefault();
    }
  };

  const handleStyleChange = useCallback(
    async (newStyle: ConversationStyle) => {
      const success = await setConversationVariables({
        conversation_style: newStyle,
      });

      if (!success) return;

      tracking.track('nova-conversation-style-changed', {
        selectedStyle: newStyle,
        source: displayOnHomepage ? 'home' : 'chat',
      });
    },
    [setConversationVariables, displayOnHomepage],
  );

  return (
    <InputBar>
      <TextFieldWrapper>
        <ChatBotTextInput
          name="message"
          id="chat-input"
          placeholder={translate(
            'conversation.conversation_input.placeholder_text',
          )}
          style="no-border"
          aria-label={translate('conversation.conversation_input.a11y_label')}
          autoComplete="off"
          ref={inputRef}
          value={currentInput}
          onChange={handleInputChange}
          multiline={true}
          onKeyDown={onKeyDown}
          $displayOnHomepage={displayOnHomepage}
        />
      </TextFieldWrapper>
      <ControlsContainer>
        {stylesEnabled && (
          <StyleSelectContainer>
            <StyleSelect
              onHomeScreen={displayOnHomepage}
              default={conversationVariables?.conversation_style}
              isLoading={isLoadingConversationVariables}
              onStyleChange={handleStyleChange}
            />
            {conversationVariablesError?.type === 'update' && (
              <StyleSelectErrorContainer role="alert">
                <ErrorIcon />
                <ErrorText>
                  {translate(
                    'conversation.conversation_style_select.error_text',
                  )}
                </ErrorText>
              </StyleSelectErrorContainer>
            )}
          </StyleSelectContainer>
        )}
        {!isInMSTeams &&
        isNovaVoiceEnabled &&
        currentInput === '' &&
        !suppressVoiceForOnboarding ? (
          <VoiceButton
            type="submit"
            icon="voice"
            size="medium"
            variant="primary"
            disabled={!canSubmit || isLoadingConversationVariables}
            ariaLabel={translate('conversation.voice_button.label')}
            screenreaderText={translate('conversation.voice_button.label')}
            onClick={navigateToVoice}
            className="chatbot-input-voice-button"
          />
        ) : (
          <ChatSubmitButton
            type="submit"
            icon="arrow-right"
            size="medium"
            variant="primary"
            disabled={
              currentInput === '' ||
              !canSubmit ||
              isLoadingConversationVariables
            }
            ariaLabel={translate('conversation.submit_button.label')}
            screenreaderText={translate('conversation.submit_button.label')}
            onClick={handleOnSubmit}
            className="chatbot-input-submit-button"
          />
        )}
      </ControlsContainer>
    </InputBar>
  );
};
